import { useState, useRef, useCallback, useEffect } from 'react';
import { Pose, Results, POSE_CONNECTIONS } from '@mediapipe/pose';
import { Camera } from '@mediapipe/camera_utils';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import { logger } from '@/lib/logger';

export interface PostureMetrics {
  trunkAngle: number;
  armSwingScore: number;
  cadence: number;
  footLandingType: 'heel' | 'midfoot' | 'forefoot';
  headPositionScore: number;
  overallScore: number;
}

export interface PostureIssue {
  type: 'trunk' | 'arms' | 'cadence' | 'foot' | 'head';
  severity: 'low' | 'medium' | 'high';
  message: string;
}

export const usePostureDetection = () => {
  const [isDetecting, setIsDetecting] = useState(false);
  const [metrics, setMetrics] = useState<PostureMetrics | null>(null);
  const [issues, setIssues] = useState<PostureIssue[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const poseRef = useRef<Pose | null>(null);
  const cameraRef = useRef<Camera | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const landmarksHistory = useRef<any[]>([]);
  const lastStepTime = useRef<number>(0);
  const stepCount = useRef<number>(0);

  const calculateTrunkAngle = (landmarks: any[]) => {
    const shoulder = landmarks[11]; // Left shoulder
    const hip = landmarks[23]; // Left hip
    
    if (!shoulder || !hip) return 0;
    
    const deltaY = hip.y - shoulder.y;
    const deltaX = hip.x - shoulder.x;
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
    
    return Math.abs(angle - 90); // 0° = vertical, positive = forward lean
  };

  const calculateArmSwingScore = (landmarks: any[]) => {
    const leftShoulder = landmarks[11];
    const leftElbow = landmarks[13];
    const leftWrist = landmarks[15];
    
    if (!leftShoulder || !leftElbow || !leftWrist) return 50;
    
    // Calculate elbow angle
    const upperArm = {
      x: leftElbow.x - leftShoulder.x,
      y: leftElbow.y - leftShoulder.y
    };
    const foreArm = {
      x: leftWrist.x - leftElbow.x,
      y: leftWrist.y - leftElbow.y
    };
    
    const dotProduct = upperArm.x * foreArm.x + upperArm.y * foreArm.y;
    const upperArmMag = Math.sqrt(upperArm.x ** 2 + upperArm.y ** 2);
    const foreArmMag = Math.sqrt(foreArm.x ** 2 + foreArm.y ** 2);
    
    const angle = Math.acos(dotProduct / (upperArmMag * foreArmMag)) * (180 / Math.PI);
    
    // Ideal elbow angle is around 90°
    const deviation = Math.abs(angle - 90);
    return Math.max(0, 100 - deviation * 2);
  };

  const detectFootLanding = (landmarks: any[]): 'heel' | 'midfoot' | 'forefoot' => {
    const ankle = landmarks[27]; // Left ankle
    const heel = landmarks[29]; // Left heel
    const toes = landmarks[31]; // Left foot index
    
    if (!ankle || !heel || !toes) return 'midfoot';
    
    const heelHeight = heel.y;
    const toesHeight = toes.y;
    
    if (heelHeight < toesHeight - 0.02) return 'heel';
    if (toesHeight < heelHeight - 0.02) return 'forefoot';
    return 'midfoot';
  };

  const calculateHeadPositionScore = (landmarks: any[]) => {
    const nose = landmarks[0];
    const shoulder = landmarks[11];
    
    if (!nose || !shoulder) return 50;
    
    const verticalAlignment = Math.abs(nose.x - shoulder.x);
    return Math.max(0, 100 - verticalAlignment * 200);
  };

  const calculateCadence = () => {
    const now = Date.now();
    const timeSinceLastStep = (now - lastStepTime.current) / 1000; // seconds
    
    if (timeSinceLastStep < 0.3) return 0; // Too fast, ignore
    
    const stepsPerSecond = 1 / timeSinceLastStep;
    return Math.round(stepsPerSecond * 60);
  };

  const analyzePosture = (landmarks: any[]) => {
    const trunkAngle = calculateTrunkAngle(landmarks);
    const armSwingScore = calculateArmSwingScore(landmarks);
    const footLandingType = detectFootLanding(landmarks);
    const headPositionScore = calculateHeadPositionScore(landmarks);
    const cadence = calculateCadence();

    // Calculate overall score
    const overallScore = Math.round(
      (armSwingScore + headPositionScore + 
       (trunkAngle >= 5 && trunkAngle <= 10 ? 100 : Math.max(0, 100 - Math.abs(trunkAngle - 7.5) * 10)) +
       (footLandingType === 'midfoot' ? 100 : 60)) / 4
    );

    const newMetrics: PostureMetrics = {
      trunkAngle,
      armSwingScore,
      cadence,
      footLandingType,
      headPositionScore,
      overallScore
    };

    setMetrics(newMetrics);

    // Detect issues
    const newIssues: PostureIssue[] = [];

    if (trunkAngle > 15) {
      newIssues.push({
        type: 'trunk',
        severity: 'high',
        message: 'Você está inclinado demais para frente. Endireite o tronco.'
      });
    } else if (trunkAngle < 5) {
      newIssues.push({
        type: 'trunk',
        severity: 'medium',
        message: 'Incline um pouco mais o tronco para frente (5-10°).'
      });
    }

    if (armSwingScore < 60) {
      newIssues.push({
        type: 'arms',
        severity: 'medium',
        message: 'Mantenha os cotovelos em 90° e balance os braços naturalmente.'
      });
    }

    if (footLandingType === 'heel') {
      newIssues.push({
        type: 'foot',
        severity: 'high',
        message: 'Evite aterrissar com o calcanhar. Tente usar o médio-pé.'
      });
    }

    if (headPositionScore < 60) {
      newIssues.push({
        type: 'head',
        severity: 'low',
        message: 'Mantenha a cabeça alinhada com a coluna e olhe para frente.'
      });
    }

    if (cadence > 0 && (cadence < 160 || cadence > 190)) {
      newIssues.push({
        type: 'cadence',
        severity: 'medium',
        message: `Cadência atual: ${cadence} passos/min. Ideal: 170-180.`
      });
    }

    setIssues(newIssues);
  };

  const onResults = useCallback((results: Results) => {
    if (!canvasRef.current || !results.poseLandmarks) return;

    const canvasCtx = canvasRef.current.getContext('2d');
    if (!canvasCtx) return;

    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    // Draw the pose skeleton
    canvasCtx.drawImage(results.image, 0, 0, canvasRef.current.width, canvasRef.current.height);
    
    drawConnectors(canvasCtx, results.poseLandmarks, POSE_CONNECTIONS, {
      color: '#00ff00',
      lineWidth: 4
    });
    
    drawLandmarks(canvasCtx, results.poseLandmarks, {
      color: '#ff0000',
      lineWidth: 2
    });

    canvasCtx.restore();

    // Store landmarks for analysis
    landmarksHistory.current.push(results.poseLandmarks);
    if (landmarksHistory.current.length > 30) {
      landmarksHistory.current.shift();
    }

    // Analyze every 10 frames
    if (landmarksHistory.current.length % 10 === 0) {
      analyzePosture(results.poseLandmarks);
    }
  }, []);

  const initializePose = useCallback(async (videoElement: HTMLVideoElement, canvasElement: HTMLCanvasElement) => {
    try {
      videoRef.current = videoElement;
      canvasRef.current = canvasElement;

      const pose = new Pose({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
        }
      });

      pose.setOptions({
        modelComplexity: 1,
        smoothLandmarks: true,
        enableSegmentation: false,
        smoothSegmentation: false,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      pose.onResults(onResults);
      poseRef.current = pose;

      const camera = new Camera(videoElement, {
        onFrame: async () => {
          if (poseRef.current) {
            await poseRef.current.send({ image: videoElement });
          }
        },
        width: 640,
        height: 480
      });

      cameraRef.current = camera;
      setIsInitialized(true);
      
      logger.info('MediaPipe Pose initialized successfully', { 
        component: 'PostureDetection' 
      });
    } catch (error) {
      logger.error('Error initializing pose detection', error, {
        component: 'PostureDetection',
        action: 'initializePose'
      });
      throw error;
    }
  }, [onResults]);

  const startDetection = useCallback(async () => {
    if (!cameraRef.current || !isInitialized) {
      logger.error('Camera not initialized', undefined, {
        component: 'PostureDetection',
        action: 'startDetection'
      });
      return;
    }

    try {
      await cameraRef.current.start();
      setIsDetecting(true);
      lastStepTime.current = Date.now();
      stepCount.current = 0;
      logger.info('Posture detection started successfully', {
        component: 'PostureDetection'
      });
    } catch (error) {
      logger.error('Error starting detection', error, {
        component: 'PostureDetection',
        action: 'startDetection'
      });
      throw error;
    }
  }, [isInitialized]);

  const stopDetection = useCallback(() => {
    if (cameraRef.current) {
      cameraRef.current.stop();
      setIsDetecting(false);
      logger.info('Posture detection stopped', {
        component: 'PostureDetection'
      });
    }
  }, []);

  const cleanup = useCallback(() => {
    stopDetection();
    if (poseRef.current) {
      poseRef.current.close();
      poseRef.current = null;
    }
    landmarksHistory.current = [];
    setIsInitialized(false);
  }, [stopDetection]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isDetecting,
    isInitialized,
    metrics,
    issues,
    initializePose,
    startDetection,
    stopDetection,
    cleanup,
    landmarksHistory: landmarksHistory.current
  };
};
